import type { StudyForm, FormField } from '../components/types';
import type { StudyFormWithFields } from '@/types/studyForms';
import { FORM_TYPE_SUBFORM } from '@/utils/formUtils';
import type { DemoDataSettings } from '../types/demoDataSettings';
import { DEFAULT_DEMO_DATA_SETTINGS } from '../types/demoDataSettings';
import { parseSubjectIdTemplate } from '../utils/subjectIdTemplateParser';

// Required metadata columns for main forms
const MAIN_FORM_METADATA_COLUMNS = ['subjectid', 'subjectnumber', 'siteid', 'formid'];

// Required metadata columns for subforms (includes mainFormId)
const SUBFORM_METADATA_COLUMNS = ['subjectid', 'subjectnumber', 'siteid', 'formid', 'mainformid'];

/**
 * Check if form is a subform (has parent or type is SUBFORM)
 */
function isSubform(form: StudyForm | StudyFormWithFields): boolean {
  // Check if form has parent property (FormTreeNode)
  if ('parent' in form && form.parent) {
    return true;
  }
  // Check form type
  return form.type === FORM_TYPE_SUBFORM;
}

/**
 * Get parent form ID if form is a subform
 */
function getParentFormId(form: StudyForm | StudyFormWithFields, explicitParentId?: number): number | null {
  // If explicit parent ID is provided, use it
  if (explicitParentId !== undefined) {
    return explicitParentId;
  }

  // Check if form has parent property (FormTreeNode)
  if ('parent' in form && form.parent) {
    const parentId = (form.parent as { id?: number } | null)?.id;
    return typeof parentId === 'number' ? parentId : null;
  }
  // Check parentFormId property
  if ('parentFormId' in form && form.parentFormId) {
    return form.parentFormId;
  }
  return null;
}

/**
 * Generate metadata headers based on form type
 */
function getMetadataHeaders(form: StudyForm | StudyFormWithFields, explicitParentId?: number): string[] {
  const hasParent = isSubform(form) || explicitParentId !== undefined;
  return hasParent ? SUBFORM_METADATA_COLUMNS : MAIN_FORM_METADATA_COLUMNS;
}

/**
 * Generate metadata row values for demo data
 */
function generateMetadataRow(
  form: StudyForm | StudyFormWithFields,
  rowIndex: number,
  explicitParentId?: number,
  settings?: DemoDataSettings
): string[] {
  const demoSettings = settings || DEFAULT_DEMO_DATA_SETTINGS;
  const { clinicalSiteIds, subjectIdTemplate, numberOfRows, startSubjectNumber } = demoSettings;

  // Ensure startSubjectNumber is a valid number (fallback to 1)
  const validStartSubjectNumber = typeof startSubjectNumber === 'number' && !isNaN(startSubjectNumber) 
    ? startSubjectNumber 
    : 1;

  // Calculate which site this row belongs to (evenly distributed)
  // Example: 3 sites, 9 rows -> rows 1-3: site 0, rows 4-6: site 1, rows 7-9: site 2
  const subjectsPerSite = Math.ceil(numberOfRows / clinicalSiteIds.length);
  const siteIndex = Math.floor((rowIndex - 1) / subjectsPerSite);
  const siteId = clinicalSiteIds[Math.min(siteIndex, clinicalSiteIds.length - 1)] || clinicalSiteIds[0] || '1';

  // Calculate absolute subject number (starting from startSubjectNumber)
  // Example: startSubjectNumber=21, rowIndex=1 -> subjectNumber=21
  // Example: startSubjectNumber=21, rowIndex=2 -> subjectNumber=22
  const subjectNumber = validStartSubjectNumber + (rowIndex - 1);

  // Generate subject ID using template with absolute subject number
  const subjectId = parseSubjectIdTemplate(
    subjectIdTemplate,
    siteId,
    subjectNumber
  );

  // subjectnumber should be just the increment number without padding (1, 2, 3...)
  const subjectNumberStr = String(subjectNumber);

  const metadata: string[] = [
    subjectId, // subjectid
    subjectNumberStr, // subjectnumber (just the number without padding)
    siteId, // siteid
    String(form.id), // formid
  ];

  // Add mainFormId for subforms
  const hasParent = isSubform(form) || explicitParentId !== undefined;
  if (hasParent) {
    const parentFormId = getParentFormId(form, explicitParentId);
    if (parentFormId) {
      metadata.push(String(parentFormId)); // mainformid
    } else {
      // If parent ID is not available, use a placeholder (should not happen in real scenario)
      metadata.push(''); // Empty mainformid - will cause validation error, but better than crashing
    }
  }

  return metadata;
}

/**
 * Extract fields from form's json_schema with full metadata
 */
type FormFieldWithSchema = FormField & {
  question?: SchemaFieldItem['question'];
  input?: SchemaFieldItem['input'];
  rawField?: unknown;
};

type SchemaFieldItem = {
  dataPoint?: string;
  subForm?: unknown;
  question?: {
    label?: string;
    list?: unknown;
    min?: unknown;
    max?: unknown;
    note?: unknown;
  };
  input?: {
    type?: unknown;
    format?: unknown;
    display?: unknown;
    min?: unknown;
    max?: unknown;
    step?: unknown;
  };
};

type SchemaPropertyDef = {
  type?: unknown;
  format?: unknown;
  enum?: unknown;
  title?: unknown;
};

function extractFormFields(form: StudyForm | StudyFormWithFields): FormFieldWithSchema[] {
  const fields: FormFieldWithSchema[] = [];

  try {
    const schema = form.json_schema;

    if (!schema) {
      return fields;
    }

    // Handle new structure with fieldsArray
    if (schema.fieldsArray && Array.isArray(schema.fieldsArray)) {
      schema.fieldsArray.forEach((fieldItemRaw: unknown) => {
        const fieldItem = fieldItemRaw as SchemaFieldItem;
        // Skip subform entries
        if (fieldItem.subForm) {
          return;
        }

        // Map field types
        let fieldType: FormField['type'] = 'text';
        if (fieldItem.input && typeof fieldItem.input === 'object') {
          const inputType = fieldItem.input.type;

          if (inputType === 'text') {
            if (
              fieldItem.question?.list &&
              Array.isArray(fieldItem.question.list)
            ) {
              fieldType = 'select';
            } else {
              fieldType = 'text';
            }
          } else if (inputType === 'number') {
            fieldType = 'number';
          } else if (inputType === 'date') {
            fieldType = 'date';
          } else if (inputType === 'time') {
            fieldType = 'time';
          } else if (inputType === 'radio') {
            fieldType = 'radio';
          }
        }

        fields.push({
          dataPoint: fieldItem.dataPoint || '',
          label: (typeof fieldItem.question?.label === 'string' && fieldItem.question.label) || fieldItem.dataPoint || '',
          type: fieldType,
          required: false,
          allowedValues: (() => {
            const list = fieldItem.question?.list;
            const display = fieldItem.input?.display;
            const raw = Array.isArray(list) ? list : Array.isArray(display) ? display : [];
            const values = raw.filter((v): v is string => typeof v === 'string');
            return values.length > 0 ? values : undefined;
          })(),
          format: typeof fieldItem.input?.format === 'string' ? fieldItem.input.format : undefined,
          // Preserve original structure for better demo generation
          question: fieldItem.question,
          input: fieldItem.input,
          rawField: fieldItem,
        });
      });
    }
    // Fallback for old structure with properties
    else if (schema.properties && typeof schema.properties === 'object') {
      const properties = schema.properties;
      const requiredFields = schema.required || [];

      Object.entries(properties as Record<string, SchemaPropertyDef>).forEach(
        ([fieldName, fieldDef]) => {
        let fieldType: FormField['type'] = 'text';
        if (fieldDef.type === 'string') {
          if (fieldDef.format === 'date') {
            fieldType = 'date';
          } else if (fieldDef.format === 'time') {
            fieldType = 'time';
          } else if (fieldDef.enum) {
            fieldType = 'select';
          } else {
            fieldType = 'text';
          }
        } else if (
          fieldDef.type === 'number' ||
          fieldDef.type === 'integer'
        ) {
          fieldType = 'number';
        } else if (fieldDef.type === 'boolean') {
          fieldType = 'radio';
        }

        fields.push({
          dataPoint: fieldName,
          label: (typeof fieldDef.title === 'string' && fieldDef.title) || fieldName,
          type: fieldType,
          required: requiredFields.includes(fieldName),
          allowedValues: (() => {
            const raw = fieldDef.enum;
            if (!Array.isArray(raw)) return undefined;
            const values = raw.filter((v): v is string => typeof v === 'string');
            return values.length > 0 ? values : undefined;
          })(),
          format: typeof fieldDef.format === 'string' ? fieldDef.format : undefined,
        });
      });
    }
  } catch (error) {
    console.error('Error extracting form fields:', error);
  }

  return fields;
}

/**
 * Escape CSV value
 */
function escapeCsvValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Generate CSV content from headers and rows
 */
function generateCsvContent(headers: string[], rows: string[][]): string {
  const csvRows: string[] = [];

  // Add headers
  csvRows.push(headers.map(escapeCsvValue).join(','));

  // Add rows
  rows.forEach(row => {
    csvRows.push(row.map(escapeCsvValue).join(','));
  });

  return csvRows.join('\n');
}

/**
 * Generate empty CSV template for a form
 * @param form - The form to generate CSV for
 * @param parentFormId - Optional explicit parent form ID (for forms with multiple parents)
 */
export function generateFormTemplateCsv(
  form: StudyForm | StudyFormWithFields,
  parentFormId?: number
): string {
  const fields = extractFormFields(form);

  if (fields.length === 0) {
    // Return CSV with just metadata headers if no fields
    const metadataHeaders = getMetadataHeaders(form, parentFormId);
    return generateCsvContent(metadataHeaders, []);
  }

  // Get metadata headers and form field headers
  const metadataHeaders = getMetadataHeaders(form, parentFormId);
  const fieldHeaders = fields.map(field => field.dataPoint);

  // Combine: metadata columns first, then form fields
  const headers = [...metadataHeaders, ...fieldHeaders];

  // Empty template - just headers, no data rows
  return generateCsvContent(headers, []);
}

/**
 * Generate demo CSV data for a form
 * @param form - The form to generate CSV for
 * @param parentFormId - Optional explicit parent form ID (for forms with multiple parents)
 * @param settings - Optional demo data settings (uses defaults if not provided)
 */
export function generateFormDemoCsv(
  form: StudyForm | StudyFormWithFields,
  parentFormId?: number,
  settings?: DemoDataSettings
): string {
  const fields = extractFormFields(form);
  const demoSettings = settings || DEFAULT_DEMO_DATA_SETTINGS;
  const { numberOfRows } = demoSettings;

  // Get metadata headers and form field headers
  const metadataHeaders = getMetadataHeaders(form, parentFormId);
  const fieldHeaders = fields.map(field => field.dataPoint);

  // Combine: metadata columns first, then form fields
  const headers = [...metadataHeaders, ...fieldHeaders];
  const rows: string[][] = [];

  // Generate rows of demo data based on settings
  for (let i = 1; i <= numberOfRows; i++) {
    // Generate metadata row
    const metadataRow = generateMetadataRow(form, i, parentFormId, demoSettings);
    
    // Extract subjectid from metadata row (first column)
    const subjectId = metadataRow[0];

    // Generate form field values
    const fieldValues = fields.map(field => {
      return generateDemoValue(field, i, subjectId);
    });

    // Combine metadata and field values
    const row = [...metadataRow, ...fieldValues];
    rows.push(row);
  }

  return generateCsvContent(headers, rows);
}

/**
 * Generate demo value for a field based on its type and metadata
 */
function generateDemoValue(
  field: FormFieldWithSchema,
  rowIndex: number,
  subjectId?: string
): string {
  const { type, dataPoint, allowedValues, question, input, label } = field;
  const dataPointLower = dataPoint.toLowerCase();
  const labelLower = (label || '').toLowerCase();

  // Check if field is PATIENT_ID - use subjectid from metadata
  if (dataPointLower === 'patient_id' || dataPointLower === 'patientid') {
    return subjectId || '';
  }

  // Check for subject/patient ID pattern (01-001 format)
  if (
    (dataPointLower.includes('subject') || dataPointLower.includes('patient')) &&
    (dataPointLower.includes('id') || labelLower.includes('subject') || labelLower.includes('patient'))
  ) {
    // If subjectId is provided, use it; otherwise generate old format
    if (subjectId) {
      return subjectId;
    }
    // Generate subject ID in format: 01-001, 01-002, etc. (fallback)
    const siteNum = String(Math.floor((rowIndex - 1) / 25) + 1).padStart(2, '0');
    const subjectNum = String(((rowIndex - 1) % 25) + 1).padStart(3, '0');
    return `${siteNum}-${subjectNum}`;
  }

  switch (type) {
    case 'number': {
      // Check if there are constraints in question or input
      const minValueRaw = input?.min ?? question?.min ?? 0;
      const maxValueRaw = input?.max ?? question?.max ?? 1000;
      const stepRaw = input?.step ?? 1;
      const minValue = typeof minValueRaw === 'number' ? minValueRaw : 0;
      const maxValue = typeof maxValueRaw === 'number' ? maxValueRaw : 1000;
      const step = typeof stepRaw === 'number' && stepRaw > 0 ? stepRaw : 1;

      // Generate realistic number within range
      const range = maxValue - minValue;
      const randomValue = minValue + Math.floor(Math.random() * range);
      return String(Math.round(randomValue / step) * step);
    }

    case 'date': {
      // Generate date in format YYYY-MM-DD
      // Use realistic date range (e.g., last 2 years)
      const currentYear = new Date().getFullYear();
      const year = currentYear - 1 + Math.floor(Math.random() * 2);
      const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
      const day = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    case 'time': {
      // Generate time in format HH:MM
      const hours = String(Math.floor(Math.random() * 24)).padStart(2, '0');
      const minutes = String(Math.floor(Math.random() * 60)).padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    case 'select':
    case 'radio':
      {
        // Use allowed values if available
        if (allowedValues && allowedValues.length > 0) {
          const index = (rowIndex - 1) % allowedValues.length;
          return allowedValues[index] || '';
        }
        // Check question.list or input.display
        const valuesRaw = Array.isArray(question?.list)
          ? question?.list
          : Array.isArray(input?.display)
            ? input?.display
            : [];
        const values = valuesRaw.filter((v): v is string => typeof v === 'string');
        if (values.length > 0) {
          const index = (rowIndex - 1) % values.length;
          return values[index] || '';
        }
        return `Option ${rowIndex % 5 + 1}`;
      }

    case 'text':
    default:
      // Generate text based on dataPoint name and label
      if (dataPointLower.includes('id') && !dataPointLower.includes('subject') && !dataPointLower.includes('patient')) {
        return `ID-${String(rowIndex).padStart(4, '0')}`;
      }
      if (dataPointLower.includes('name') || labelLower.includes('name')) {
        const firstNames = ['John', 'Jane', 'Robert', 'Mary', 'Michael', 'Sarah', 'David', 'Emily'];
        const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis'];
        const firstName = firstNames[(rowIndex - 1) % firstNames.length];
        const lastName = lastNames[Math.floor((rowIndex - 1) / firstNames.length) % lastNames.length];
        return `${firstName} ${lastName}`;
      }
      if (dataPointLower.includes('email') || labelLower.includes('email')) {
        return `user${rowIndex}@example.com`;
      }
      if (dataPointLower.includes('age') || labelLower.includes('age')) {
        return String(18 + Math.floor(Math.random() * 82)); // Age 18-100
      }
      if (dataPointLower.includes('gender') || labelLower.includes('gender') || labelLower.includes('sex')) {
        const genders = ['Male', 'Female', 'Other'];
        return genders[(rowIndex - 1) % genders.length];
      }
      if (dataPointLower.includes('country') || labelLower.includes('country')) {
        const countries = ['USA', 'Canada', 'UK', 'Germany', 'France', 'Italy', 'Spain', 'Australia'];
        return countries[(rowIndex - 1) % countries.length];
      }
      if (typeof question?.note === 'string' && question.note) {
        // Use note as hint for generation
        const note = question.note.toLowerCase();
        if (note.includes('date') || note.includes('when')) {
          const year = 2020 + Math.floor(Math.random() * 5);
          const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
          const day = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        if (note.includes('number') || note.includes('count')) {
          return String(Math.floor(Math.random() * 100) + 1);
        }
      }
      // Default: generate based on label or dataPoint
      if (label && label !== dataPoint) {
        return `${label} ${rowIndex}`;
      }
      return `Value ${rowIndex}`;
  }
}

/**
 * Download CSV file
 */
export function downloadCsvFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

